import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Bell,
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronRight,
  ChevronDown,
  Clock,
  Users,
  Calendar,
  Wallet,
  CheckCircle2,
  FileText,
  TrendingUp,
  CalendarDays,
  Brain,
  CreditCard,
  Lightbulb,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { Alerte, AlerteAction } from '@/hooks/useAlertes';
import { usePlanningStore } from '@/stores/planningStore';

interface PilotagePanelExpandedProps {
  isVisible: boolean;
  onCollapse: () => void;
  alertes: Alerte[];
  compteurs: {
    critique: number;
    attention: number;
    info: number;
    suggestion: number;
    total: number;
  };
  loading: boolean;
  onRefresh?: () => void;
  onMarquerLue?: (alerteId: string) => void;
}

// Types de filtres
type FilterType = 'tous' | 'critique' | 'attention' | 'info';

// Icône selon la catégorie
const getCategorieIcon = (categorie: string) => {
  switch (categorie) {
    case 'budget':
      return Wallet;
    case 'retard':
      return Clock;
    case 'equipe':
      return Users;
    case 'rotation':
      return Calendar;
    case 'fin_chantier':
      return CalendarDays;
    case 'facture':
      return FileText;
    case 'performance':
      return TrendingUp;
    case 'planning':
      return Calendar;
    case 'paiement':
      return CreditCard;
    case 'analyse_ia':
      return Brain;
    case 'devis':
      return FileText;
    default:
      return Info;
  }
};

// Icône selon le type d'alerte
const getTypeIcon = (type: 'critique' | 'attention' | 'info' | 'suggestion') => {
  switch (type) {
    case 'critique':
      return AlertTriangle;
    case 'attention':
      return AlertCircle;
    case 'info':
    case 'suggestion':
      return Info;
  }
};

// Couleur selon le type
const getTypeStyles = (type: 'critique' | 'attention' | 'info' | 'suggestion') => {
  switch (type) {
    case 'critique':
      return {
        iconBg: 'bg-red-500/10',
        icon: 'text-red-500',
        badge: 'bg-red-500/20 text-red-400',
        button: 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30',
        border: 'border-red-500/30',
      };
    case 'attention':
      return {
        iconBg: 'bg-yellow-500/10',
        icon: 'text-yellow-500',
        badge: 'bg-yellow-500/20 text-yellow-400',
        button: 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 border border-yellow-500/30',
        border: 'border-yellow-500/30',
      };
    case 'info':
      return {
        iconBg: 'bg-blue-500/10',
        icon: 'text-blue-500',
        badge: 'bg-blue-500/20 text-blue-400',
        button: 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/30',
        border: 'border-blue-500/30',
      };
    case 'suggestion':
      return {
        iconBg: 'bg-purple-500/10',
        icon: 'text-purple-500',
        badge: 'bg-purple-500/20 text-purple-400',
        button: 'bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 border border-purple-500/30',
        border: 'border-purple-500/30',
      };
  }
};


/**
 * Panneau déplié du Centre de Pilotage - Design épuré avec dépliage
 */
const PilotagePanelExpanded = ({
  isVisible,
  onCollapse,
  alertes,
  compteurs,
  loading,
  onRefresh,
  onMarquerLue,
}: PilotagePanelExpandedProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { openPlanningToday } = usePlanningStore();

  // État pour le filtre actif
  const [activeFilter, setActiveFilter] = useState<FilterType>('tous');

  // État pour les alertes dépliées
  const [expandedAlerts, setExpandedAlerts] = useState<Set<string>>(new Set());

  // État pour le modal de clôture simplifié
  const [closureDialog, setClosureDialog] = useState<{
    open: boolean;
    chantierId?: string;
    chantierNom?: string;
  }>({ open: false });
  const [dateFinReelle, setDateFinReelle] = useState(new Date().toISOString().split('T')[0]);
  const [closureLoading, setClosureLoading] = useState(false);

  // Filtrer les alertes selon le filtre actif
  const alertesFiltrees = activeFilter === 'tous'
    ? alertes
    : alertes.filter((a) => a.type === activeFilter);

  // Toggle l'expansion d'une alerte
  const toggleAlertExpansion = (alerteId: string) => {
    setExpandedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alerteId)) {
        newSet.delete(alerteId);
      } else {
        newSet.add(alerteId);
      }
      return newSet;
    });
  };

  if (!isVisible) return null;

  // Gérer les actions sur une alerte
  const handleAction = async (action: AlerteAction, alerte: Alerte) => {
    // Marquer comme lue après avoir cliqué sur une action
    onMarquerLue?.(alerte.id);

    if (action.type === 'navigate' && action.destination) {
      if (action.params?.chantierId) {
        navigate(action.destination, { state: { chantierId: action.params.chantierId } });
      } else {
        navigate(action.destination);
      }
      onCollapse();
      return;
    }

    if (action.type === 'modal' && action.destination === 'modal_cloture') {
      setClosureDialog({
        open: true,
        chantierId: action.params?.chantier_id,
        chantierNom: action.params?.chantier_nom,
      });
      return;
    }

    if (action.type === 'function' && action.params) {
      const { action: actionType, ...params } = action.params;

      switch (actionType) {
        case 'accepter_devis':
          try {
            await supabase.from('devis').update({ statut: 'accepte' }).eq('id', params.devis_id);
            toast({ title: 'Devis marqué comme accepté' });
            onRefresh?.();
          } catch {
            toast({ title: 'Erreur', variant: 'destructive' });
          }
          break;

        case 'refuser_devis':
          try {
            await supabase.from('devis').update({ statut: 'refuse' }).eq('id', params.devis_id);
            toast({ title: 'Devis marqué comme refusé' });
            onRefresh?.();
          } catch {
            toast({ title: 'Erreur', variant: 'destructive' });
          }
          break;

        case 'telecharger_bilan':
          navigate(`/projects/${params.chantier_id}?tab=ai-analysis&action=bilan`);
          onCollapse();
          break;

        case 'open_planning':
          onCollapse();
          openPlanningToday();
          break;
      }
    }
  };

  // Clôturer le chantier
  const handleCloseChantier = async () => {
    if (!closureDialog.chantierId) return;

    setClosureLoading(true);
    try {
      const { error } = await supabase
        .from('chantiers')
        .update({ etat_chantier: 'termine', date_fin_reelle: dateFinReelle })
        .eq('id', closureDialog.chantierId);

      if (error) throw error;

      toast({
        title: '✅ Chantier clôturé',
        description: `${closureDialog.chantierNom} a été marqué comme terminé.`,
      });
      setClosureDialog({ open: false });
      onRefresh?.();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de clôturer le chantier.',
        variant: 'destructive',
      });
    } finally {
      setClosureLoading(false);
    }
  };

  return (
    <div
      className={cn(
        'fixed right-0 top-0 bottom-0 z-[60]',
        'w-[480px]',
        'bg-[#0F0F14]/98 backdrop-blur-xl',
        'border-l border-white/[0.08]',
        'shadow-2xl shadow-black/50',
        'animate-in slide-in-from-right duration-300'
      )}
    >
      {/* ═══ HEADER ═══ */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.05]">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <Bell className="h-4 w-4 text-white" />
          </div>
          <h2 className="font-semibold text-lg text-white">Centre de pilotage</h2>
        </div>
        <div className="flex items-center gap-3">
          {/* Badges compteurs */}
          <div className="flex gap-2">
            {compteurs.critique > 0 && (
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                {compteurs.critique} critique{compteurs.critique > 1 ? 's' : ''}
              </span>
            )}
            {compteurs.attention > 0 && (
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                {compteurs.attention} attention
              </span>
            )}
            {compteurs.info > 0 && (
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                {compteurs.info} info{compteurs.info > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onCollapse} className="text-white/60 hover:text-white hover:bg-white/10">
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* ═══ FILTRES ═══ */}
      <div className="px-6 py-3 border-b border-white/[0.03] flex items-center gap-4">
        <span className="text-sm text-white/40">Filtrer :</span>
        <button
          onClick={() => setActiveFilter('tous')}
          className={cn(
            'px-3 py-1.5 text-xs rounded-md transition-colors',
            activeFilter === 'tous'
              ? 'bg-white/10 text-white'
              : 'text-white/50 hover:bg-white/[0.05] hover:text-white/70'
          )}
        >
          Tous
        </button>
        <button
          onClick={() => setActiveFilter('critique')}
          className={cn(
            'px-3 py-1.5 text-xs rounded-md transition-colors',
            activeFilter === 'critique'
              ? 'bg-red-500/20 text-red-400'
              : 'text-white/50 hover:bg-white/[0.05] hover:text-white/70'
          )}
        >
          Critiques
        </button>
        <button
          onClick={() => setActiveFilter('attention')}
          className={cn(
            'px-3 py-1.5 text-xs rounded-md transition-colors',
            activeFilter === 'attention'
              ? 'bg-yellow-500/20 text-yellow-400'
              : 'text-white/50 hover:bg-white/[0.05] hover:text-white/70'
          )}
        >
          Attention
        </button>
        <button
          onClick={() => setActiveFilter('info')}
          className={cn(
            'px-3 py-1.5 text-xs rounded-md transition-colors',
            activeFilter === 'info'
              ? 'bg-blue-500/20 text-blue-400'
              : 'text-white/50 hover:bg-white/[0.05] hover:text-white/70'
          )}
        >
          Info
        </button>
      </div>

      {/* ═══ LISTE DES ALERTES ═══ */}
      <ScrollArea className="h-[calc(100vh-140px)]">
        <div className="p-4 space-y-3">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-4 animate-pulse">
                  <div className="h-10 w-10 rounded-lg bg-white/10" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-white/10 rounded w-1/4" />
                    <div className="h-4 bg-white/10 rounded w-3/4" />
                    <div className="h-3 bg-white/10 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : alertesFiltrees.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="p-4 rounded-full bg-white/5 mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
              <h3 className="font-semibold text-lg text-white mb-1">Tout est sous contrôle</h3>
              <p className="text-sm text-white/50 max-w-[280px]">
                Aucune alerte à afficher pour le moment. Continuez comme ça !
              </p>
            </div>
          ) : (
            alertesFiltrees.map((alerte) => {
              const styles = getTypeStyles(alerte.type);
              const TypeIcon = getTypeIcon(alerte.type);
              const CategoryIcon = getCategorieIcon(alerte.categorie);
              const hasActions = alerte.actions && alerte.actions.length > 0;
              const isExpanded = expandedAlerts.has(alerte.id);

              return (
                <div
                  key={alerte.id}
                  className={cn(
                    'rounded-xl border transition-all',
                    styles.iconBg,
                    styles.border
                  )}
                >
                  {/* En-tête cliquable */}
                  <div
                    className="flex items-start gap-4 p-4 cursor-pointer hover:bg-white/[0.02]"
                    onClick={() => toggleAlertExpansion(alerte.id)}
                  >
                    {/* Icône type */}
                    <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0', styles.iconBg)}>
                      <TypeIcon className={cn('h-5 w-5', styles.icon)} />
                    </div>

                    {/* Contenu */}
                    <div className="flex-1 min-w-0">
                      {/* Badge */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className={cn('px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase', styles.badge)}>
                          {alerte.type === 'critique' ? 'CRITIQUE' :
                           alerte.type === 'attention' ? 'ATTENTION' :
                           alerte.type === 'suggestion' ? 'SUGGESTION' : 'INFO'}
                        </span>
                      </div>

                      {/* Titre */}
                      <p className="text-white font-medium text-sm leading-snug">{alerte.titre}</p>

                      {/* Description (tronquée si non déplié) */}
                      <p className={cn(
                        'text-sm text-white/50 mt-0.5',
                        !isExpanded && 'line-clamp-2'
                      )}>
                        {alerte.description}
                      </p>
                    </div>

                    {/* Bouton marquer comme lu + Flèche */}
                    <div className="flex-shrink-0 flex items-center gap-2">
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              className={cn(
                                'p-1.5 rounded-lg border-2 transition-all',
                                'bg-white/5 border-white/20',
                                'hover:bg-green-500/20 hover:border-green-500 hover:text-green-500',
                                'text-white/50'
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (alerte.id.startsWith('aggregated-') && alerte.donnees?.alertesGroupees) {
                                  (alerte.donnees.alertesGroupees as Alerte[]).forEach(
                                    (sub: Alerte) => onMarquerLue?.(sub.id)
                                  );
                                } else {
                                  onMarquerLue?.(alerte.id);
                                }
                              }}
                            >
                              <Check className="w-4 h-4" strokeWidth={3} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="text-xs">
                            Marquer comme lu
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-white/40" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-white/40" />
                      )}
                    </div>
                  </div>

                  {/* Contenu déplié */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 border-t border-white/[0.05] mt-0">
                      <div className="pt-3 space-y-3">
                        {/* Affichage spécial pour les membres sans affectation */}
                        {alerte.id === 'membres-sans-affectation' && alerte.donnees?.membres && (
                          <div className="space-y-2">
                            <p className="text-xs text-white/40 font-medium">
                              {alerte.donnees.membres.length} membre{alerte.donnees.membres.length > 1 ? 's' : ''} disponible{alerte.donnees.membres.length > 1 ? 's' : ''} :
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {alerte.donnees.membres.map((membre: { id: string; nom: string; specialite?: string }) => (
                                <span
                                  key={membre.id}
                                  className="inline-flex items-center px-2 py-1 rounded-md bg-yellow-500/10 text-xs text-white font-medium"
                                >
                                  {membre.nom}
                                  {membre.specialite && (
                                    <span className="ml-1 text-white/70">({membre.specialite})</span>
                                  )}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Affichage spécial pour les rotations à planifier */}
                        {alerte.id === 'rotations-semaine' && alerte.donnees?.rotations && (
                          <div className="space-y-2">
                            <p className="text-xs text-white/40 font-medium">
                              Affectations se terminant cette semaine :
                            </p>
                            <div className="space-y-1.5">
                              {alerte.donnees.rotations.map((rotation: { id: string; membre: string; chantier: string; date_fin: string }) => (
                                <div
                                  key={rotation.id}
                                  className="flex items-center justify-between px-2 py-1.5 rounded-md bg-yellow-500/10 text-xs"
                                >
                                  <span className="font-medium text-white">{rotation.membre}</span>
                                  <span className="text-white/70 font-medium">
                                    {rotation.chantier} — fin le {new Date(rotation.date_fin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Affichage spécial pour les chantiers à facturer */}
                        {alerte.id === 'chantiers-a-facturer' && alerte.donnees?.chantiers && (
                          <div className="space-y-2">
                            <p className="text-xs text-white/40 font-medium">
                              Chantiers terminés à vérifier :
                            </p>
                            <div className="space-y-1.5">
                              {alerte.donnees.chantiers.map((chantier: { id: string; nom: string; montant: number; statut_paiement: string }) => (
                                <div
                                  key={chantier.id}
                                  className="flex items-center justify-between px-2 py-1.5 rounded-md bg-blue-500/10 text-xs cursor-pointer hover:bg-blue-500/20"
                                  onClick={() => { navigate(`/financial?tab=paiement&chantier=${chantier.id}`); onCollapse(); }}
                                >
                                  <span className="font-medium text-white">{chantier.nom}</span>
                                  <span className="text-white/70 font-medium">
                                    {chantier.montant.toLocaleString('fr-FR')} € HT
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Affichage des détails budget/marge/déficit */}
                        {(alerte.id.startsWith('marge-') || alerte.id.startsWith('budget-') || alerte.id.startsWith('deficit-')) && alerte.donnees && (
                          <div className="space-y-1.5 text-xs">
                            {alerte.donnees.marge_pct !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-white/40">Marge</span>
                                <span className={cn(
                                  'font-medium',
                                  alerte.donnees.marge_pct < 0 ? 'text-red-400' :
                                  alerte.donnees.marge_pct < 18 ? 'text-yellow-400' : 'text-green-400'
                                )}>
                                  {alerte.donnees.marge_pct.toFixed(1)}%
                                  {alerte.donnees.marge_euros !== undefined && (
                                    <span className="ml-1 text-white/40">({alerte.donnees.marge_euros.toLocaleString('fr-FR')} €)</span>
                                  )}
                                </span>
                              </div>
                            )}
                            {alerte.donnees.budget_consomme_pct !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-white/40">Budget consommé</span>
                                <span className={cn(
                                  'font-medium',
                                  alerte.donnees.budget_consomme_pct > 100 ? 'text-red-400' :
                                  alerte.donnees.budget_consomme_pct > 80 ? 'text-yellow-400' : 'text-green-400'
                                )}>
                                  {alerte.donnees.budget_consomme_pct.toFixed(0)}%
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Affichage des détails retard */}
                        {alerte.id.startsWith('retard-') && alerte.donnees && (
                          <div className="space-y-1.5 text-xs">
                            <div className="flex justify-between">
                              <span className="text-white/40">Jours de retard</span>
                              <span className="font-medium text-red-400">{alerte.donnees.jours_retard} jour{alerte.donnees.jours_retard > 1 ? 's' : ''}</span>
                            </div>
                            {alerte.donnees.date_fin_prevue && (
                              <div className="flex justify-between">
                                <span className="text-white/40">Date de fin prévue</span>
                                <span className="font-medium text-white/70">{new Date(alerte.donnees.date_fin_prevue).toLocaleDateString('fr-FR')}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Affichage des détails fin proche */}
                        {alerte.id.startsWith('fin-proche-') && alerte.donnees && (
                          <div className="space-y-1.5 text-xs">
                            <div className="flex justify-between">
                              <span className="text-white/40">Jours restants</span>
                              <span className="font-medium text-yellow-400">{alerte.donnees.jours_restants} jour{alerte.donnees.jours_restants > 1 ? 's' : ''}</span>
                            </div>
                            {alerte.donnees.date_fin_prevue && (
                              <div className="flex justify-between">
                                <span className="text-white/40">Date de fin prévue</span>
                                <span className="font-medium text-white/70">{new Date(alerte.donnees.date_fin_prevue).toLocaleDateString('fr-FR')}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Rendu agrégé : liste des chantiers avec leurs propres boutons */}
                        {alerte.id.startsWith('aggregated-') && alerte.donnees?.alertesGroupees && (
                          <div className="space-y-2">
                            {(alerte.donnees.alertesGroupees as Alerte[]).map((subAlerte: Alerte) => (
                              <div key={subAlerte.id} className="bg-white/5 rounded-lg p-3">
                                <div className="min-w-0">
                                  <p className="font-semibold text-sm text-white truncate">
                                    {subAlerte.chantierNom || subAlerte.membreNom || 'Détail'}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                                    {subAlerte.description}
                                  </p>
                                </div>
                                {subAlerte.actions && subAlerte.actions.length > 0 && (
                                  <div className="flex gap-2 mt-2">
                                    {subAlerte.actions.map((action, idx) => (
                                      <Button
                                        key={idx}
                                        size="sm"
                                        variant={
                                          action.variant === 'primary' ? 'default' :
                                          action.variant === 'success' ? 'default' :
                                          action.variant === 'danger' ? 'destructive' :
                                          'outline'
                                        }
                                        className={cn(
                                          'text-xs',
                                          action.variant === 'success' && 'bg-green-600 hover:bg-green-700'
                                        )}
                                        onClick={() => handleAction(action, subAlerte)}
                                      >
                                        {action.label}
                                      </Button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Boutons d'action */}
                        {hasActions && (
                          <div className="flex gap-2 pt-2">
                            {alerte.actions!.map((action, idx) => (
                              <Button
                                key={idx}
                                size="sm"
                                variant={
                                  action.variant === 'primary' ? 'default' :
                                  action.variant === 'success' ? 'default' :
                                  action.variant === 'warning' ? 'default' :
                                  action.variant === 'danger' ? 'destructive' :
                                  'outline'
                                }
                                className={cn(
                                  'flex-1 text-xs',
                                  action.variant === 'success' && 'bg-green-600 hover:bg-green-700',
                                  action.variant === 'warning' && 'bg-amber-500 hover:bg-amber-600 text-black'
                                )}
                                onClick={() => handleAction(action, alerte)}
                              >
                                {action.label}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Modal de clôture */}
      <Dialog
        open={closureDialog.open}
        onOpenChange={(open) => setClosureDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="sm:max-w-md z-[100]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Clôturer le chantier
            </DialogTitle>
            <DialogDescription>
              Confirmer la fin de "{closureDialog.chantierNom}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="date-fin" className="text-sm font-medium">
              Date de fin réelle
            </Label>
            <Input
              id="date-fin"
              type="date"
              value={dateFinReelle}
              onChange={(e) => setDateFinReelle(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setClosureDialog({ open: false })}
              disabled={closureLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleCloseChantier}
              disabled={closureLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {closureLoading ? 'Clôture...' : 'Confirmer la clôture'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PilotagePanelExpanded;
